export type ChatConversationType = "direct" | "group" | "channel";

export interface ChatLastMessage {
  authorId: number | null;
  body: string;
  createdAt: string;
  attachmentCount: number;
}

export interface ChatConversation {
  id: number;
  type: ChatConversationType;
  name: string;
  description: string;
  createdById: number | null;
  lastMessageAt: string | null;
  archivedAt: string | null;
  createdAt: string;
  memberIds: number[];
  joined: boolean;
  lastReadAt: string | null;
  unreadCount: number;
  mentionCount: number;
  /** Direct chat mirrored to a Microsoft Teams one-on-one chat. */
  teamsLinked: boolean;
  lastMessage: ChatLastMessage | null;
}

export interface TeamsStatus {
  enabled: boolean;
  connected: boolean;
  email: string;
  displayName: string;
  lastError: string | null;
}

export interface ChatReaction {
  emoji: string;
  userIds: number[];
}

export interface ChatAttachment {
  id: number;
  fileName: string;
  contentType: string;
  sizeBytes: number;
}

export interface ChatAttachmentContent extends ChatAttachment {
  contentBase64: string;
}

export interface ChatMessage {
  id: number;
  conversationId: number;
  authorId: number | null;
  parentMessageId: number | null;
  body: string;
  editedAt: string | null;
  deletedAt: string | null;
  createdAt: string;
  replyCount: number;
  lastReplyAt: string | null;
  replyAuthorIds: number[];
  mentionUserIds: number[];
  reactions: ChatReaction[];
  attachments: ChatAttachment[];
  /** "teams" when written in Microsoft Teams. */
  source: "app" | "teams";
  /** App message that also exists in the linked Teams chat. */
  teamsSynced: boolean;
}

export interface ChatMessagePage {
  messages: ChatMessage[];
  hasMore: boolean;
}

export interface ChatThread {
  parent: ChatMessage;
  replies: ChatMessage[];
}

export interface ChatOutgoingAttachment {
  fileName: string;
  contentType: string;
  contentBase64: string;
}

export interface ChatSendMessageInput {
  body: string;
  parentMessageId?: number | null;
  mentionUserIds: number[];
  attachments: ChatOutgoingAttachment[];
}

export type ChatCreateConversationInput =
  | { type: "direct"; userId: number }
  | { type: "group"; memberIds: number[]; name?: string }
  | { type: "channel"; name: string; description?: string };

export interface ChatReactionEvent {
  messageId: number;
  conversationId: number;
  parentMessageId: number | null;
  reactions: ChatReaction[];
}

export const CHAT_QUICK_REACTIONS = ["👍", "❤️", "😂", "😮", "😢", "🎉"];

export const CHAT_MAX_ATTACHMENT_BYTES = 15 * 1024 * 1024;
